import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHero } from "@/components/site/PageHero";
import { SITE } from "@/config/site";
import { CheckCircle2, ArrowRight, MapPin, Sparkles, Users, ClipboardList } from "lucide-react";
import cleanerAssetUrl from "@/assets/service-areas-cleaner.webp";
const cleanerAsset = { url: cleanerAssetUrl };

export const Route = createFileRoute("/join-as-a-cleaner")({
  head: () => ({
    meta: [
      { title: "Join as a Cleaner: IZHUB Cleaning Services" },
      { name: "description", content: "Recruiting cleaners across Tidworth, Bulford, Larkhill, Amesbury, Ludgershall, Andover, Salisbury and Salisbury Plain. Apply through the IZHUB Cleaning Services cleaner portal for domestic, deep, end of tenancy, Airbnb, office and military march-out cleaning work." },
      { property: "og:title", content: "Join as a Cleaner: IZHUB Cleaning Services" },
      { property: "og:description", content: "We are recruiting reliable cleaners across Wiltshire and Hampshire. Apply via our cleaner portal to register your availability for local domestic, end of tenancy, Airbnb and office cleaning work." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://izhubcleaning.uk/join-as-a-cleaner" },
      { property: "og:site_name", content: "IZHUB Cleaning Services" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://izhubcleaning.uk/join-as-a-cleaner" }],
  }),
  component: JoinAsACleaner,
});

const lookingFor = [
  "Reliable and professional cleaners",
  "People with good attention to detail",
  "Cleaners who communicate clearly and respectfully",
  "People who are comfortable working in homes, offices, Airbnb properties and end of tenancy settings",
  "Cleaners who can follow instructions and cleaning checklists",
  "People who take pride in doing a job properly",
];

const workTypes = [
  "Domestic cleaning",
  "Deep cleaning",
  "End of tenancy cleaning",
  "Airbnb and short-let cleaning",
  "Office cleaning",
  "Military march-out cleaning",
  "Move-in and move-out cleaning",
];

const steps = [
  "Click the button below to access the cleaner portal.",
  "Complete your details and availability.",
  "We will review your information.",
  "If suitable, we may contact you about available cleaning jobs.",
  "You can keep your availability updated through the portal.",
];

function JoinAsACleaner() {
  return (
    <>
      <PageHero
        title={<>Join the <span className="rainbow-text">IZHUB Cleaning Services</span> Team</>}
        intro="We are looking for reliable, detail-focused cleaners who take pride in their work and want to support homes, landlords, Airbnb hosts, offices and military families across our local service areas."
        withBackground
        backgroundImage={cleanerAsset.url}
      >
        <a
          href={SITE.cleanerPortalUrl}
          className="btn-primary text-base md:text-lg !px-7 !py-3.5"
          aria-label="Apply or log in to the cleaner portal"
        >
          Apply / Login to Cleaner Portal <ArrowRight className="h-5 w-5" />
        </a>
      </PageHero>

      <section className="py-20 md:py-24">
        <div className="container-x grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:items-center">
          <div className="rounded-3xl bg-cream/90 backdrop-blur-sm border border-border p-8 md:p-10">
            <span className="grid h-12 w-12 place-items-center rounded-2xl bg-sage-soft text-primary"><Sparkles className="h-6 w-6" /></span>
            <h2 className="mt-6 font-display text-3xl md:text-4xl">Work with a local cleaning business that values quality</h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              At IZHUB Cleaning Services, we aim to provide a professional, reliable and high-standard cleaning service. We work with cleaners who are punctual, trustworthy, respectful and committed to leaving every property clean and well presented.
            </p>
          </div>
          <div className="rounded-3xl bg-primary text-primary-foreground p-8 md:p-12">
            <span className="grid h-12 w-12 place-items-center rounded-2xl bg-primary-foreground/15 text-primary-foreground"><Users className="h-6 w-6" /></span>
            <h2 className="mt-6 font-display text-3xl md:text-4xl text-primary-foreground">Who we are looking for</h2>
            <ul className="mt-6 space-y-3">
              {lookingFor.map((item) => (
                <li key={item} className="flex gap-3 text-primary-foreground/90">
                  <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0 text-primary-foreground" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-16 bg-cream/85 backdrop-blur-sm border-y border-border">
        <div className="container-x">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl">Cleaning work may include</h2>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {workTypes.map((type) => (
              <div key={type} className="flex items-start gap-3 rounded-2xl border border-border bg-card p-5">
                <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0 text-sage" />
                <span className="font-medium text-foreground/90">{type}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24">
        <div className="container-x">
          <div className="text-surface max-w-3xl mx-auto text-center">
            <h2 className="font-display text-3xl md:text-4xl">How it works</h2>
          </div>
          <ol className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-5">
            {steps.map((step, i) => (
              <li key={i} className="relative rounded-3xl border border-border bg-card p-6 text-center">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-primary text-primary-foreground text-sm font-semibold mx-auto">
                  {i + 1}
                </span>
                <p className="mt-4 text-sm text-muted-foreground">{step}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="py-16 bg-cream/85 backdrop-blur-sm border-y border-border">
        <div className="container-x">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl">Areas we cover</h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              We provide cleaning services across Tidworth, Bulford, Larkhill, Amesbury, Ludgershall, Andover, Salisbury, Salisbury Plain and nearby areas. Cleaners based in or near these areas are welcome to register their interest.
            </p>
            <div className="mt-6 flex items-start gap-2 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-sage" />
              <span>Tidworth · Bulford · Larkhill · Amesbury · Ludgershall · Andover · Salisbury · Salisbury Plain</span>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-x">
          <div className="rounded-3xl bg-primary text-primary-foreground p-8 md:p-14 text-center">
            <span className="grid h-12 w-12 place-items-center rounded-2xl bg-primary-foreground/15 text-primary-foreground mx-auto"><ClipboardList className="h-6 w-6" /></span>
            <h2 className="mt-6 font-display text-3xl md:text-4xl text-primary-foreground max-w-2xl mx-auto">Ready to join IZHUB Cleaning Services?</h2>
            <p className="mt-4 text-primary-foreground/85 max-w-lg mx-auto">Use the cleaner portal to apply, log in, update your details or manage your availability.</p>
            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
              <a
                href={SITE.cleanerPortalUrl}
                className="btn-primary text-lg !px-8 !py-4 bg-primary-foreground text-primary hover:bg-primary-foreground/90"
                aria-label="Apply or log in to the cleaner portal"
              >
                Apply / Login to Cleaner Portal <ArrowRight className="h-5 w-5" />
              </a>
              <Link to="/contact" className="btn-outline border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">
                Contact Us
              </Link>
            </div>
            <p className="mt-6 text-xs text-primary-foreground/70 max-w-xl mx-auto">
              Submitting your details does not guarantee work, but allows IZHUB Cleaning Services to review your availability and suitability for upcoming cleaning jobs.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
